import React from 'react';
import {
  ChipContainer,
  StyledCaret,
  StyledCheckmark,
  StyledImg,
  StyledIcon,
  StyledWrapper,
  StyledHedDek,
  StyledHed,
  StyledDek,
} from './Chip.styles';

type TwoLinesProps = { twoLines?: false; width?: never } | { twoLines: true; width: string };
type IconProps = {
  /**
   * Zola-UI SVG Icon
   */
  icon?: React.ReactNode;
  iconPosition?: 'top' | 'left';
};
type ImgProps = {
  /** displays image in top portion of Chip */
  imgSrc?: string;
  /** alt text for image used in top portion of Chip */
  imgAlt?: string;
};

export type ChipProps = React.HTMLAttributes<HTMLButtonElement> & {
  /** html that will render when hed is not passed */
  children?: React.ReactNode;

  /**
   * When used with withCaret, controls the direction of the caret.  Selected = false
   * means the caret is a down caret.  Selected = true means the caret is an up caret.
   *
   * Selected=true also toggles the visual state of the component from the non
   * selected state.  For zola-ui 3.0, a selected chip is filled in with a color
   * and a non-selected chip is transparent/white.
   */
  selected?: boolean;

  /**
   * Applies the _same_ visual state to the chip as if it was selected.  However,
   * does not toggle the caret state.
   */
  active?: boolean;

  /** Disables the default 1px border on the chip */
  noBorder?: boolean;

  disabled?: boolean;
  /** Applies a height of 56px for a single line */
  large?: boolean;
  /** onClick callback */
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  showCheckmark?: boolean;
  /** can be used as label */
  htmlFor?: string;
  /** displays caret to the right of text */
  withCaret?: boolean;

  /** hed & dek can be used as an alternative to children */
  hed?: string;
  dek?: string;
} & TwoLinesProps &
  IconProps &
  ImgProps;

/**
 * Chips are used in forms to make selections, filter content, or trigger actions.
 *
 * Chips are interactive elements; they are clickable, have a hover state, and have a selected state.
 *
 * Avoid using icons as much as possible. Use icons only if they provide needed additional context.
 */
const Chip = React.forwardRef<HTMLButtonElement, ChipProps>(
  (
    {
      children,
      onClick,
      showCheckmark,
      selected,
      active,
      noBorder,
      disabled,
      twoLines,
      withCaret,
      large,
      imgSrc,
      imgAlt,
      hed,
      dek,
      icon,
      iconPosition,
      ...args
    },
    ref
  ) => {
    const handleClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
      if (onClick) {
        onClick(e);
      }
    };

    return (
      <ChipContainer
        onClick={handleClick}
        selected={selected}
        active={active}
        ref={ref}
        noBorder={noBorder}
        disabled={disabled}
        twoLines={twoLines}
        withImg={Boolean(imgSrc)}
        large={large}
        // add data-* attributes so styled component props can be targeted
        data-selected={selected}
        data-active={active}
        {...args}
      >
        {imgSrc && (
          <StyledImg>
            <img src={imgSrc} alt={imgAlt} />
          </StyledImg>
        )}
        <StyledWrapper
          withImg={Boolean(imgSrc)}
          withCaret={withCaret}
          withIcon={Boolean(icon)}
          withIconPositionTop={iconPosition === 'top'}
          withHed={Boolean(hed)}
        >
          {!icon && showCheckmark && selected && <StyledCheckmark />}
          {icon && <StyledIcon className={iconPosition}>{icon}</StyledIcon>}
          {!(hed || dek) && children}
          {(hed || dek) && (
            <StyledHedDek>
              <StyledHed>{hed}</StyledHed>
              {dek && <StyledDek>{dek}</StyledDek>}
            </StyledHedDek>
          )}
          {withCaret && (
            <StyledCaret
              disabled={disabled}
              selected={selected}
              width={14}
              height={16}
              showTitle={false}
            />
          )}
        </StyledWrapper>
      </ChipContainer>
    );
  }
);

export default Chip;
